import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ArtDiscipline, ArtOpportunity, ArtistRegistry, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const DISCIPLINES: ArtDiscipline[] = ['MUSIC', 'VISUAL_ART', 'DANCE', 'THEATRE', 'LITERATURE', 'FILM', 'CRAFT', 'FASHION', 'CULINARY', 'OTHER'];

const TABS = [
  { key: 'registry', label: 'Artist registry' },
  { key: 'opportunities', label: 'Opportunities' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function ArtistsPage() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('YOUTH_OFFICER') ?? false;
  const [tab, setTab] = useState<TabKey>('registry');

  return (
    <div>
      <PageHeader
        title="Artist Registry & Opportunities"
        subtitle="Discipline-based artist registry with live opportunity matching — artists.ts."
      />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Youth & Sport Officer role — showing the public registry read-only. Editing registrations, deactivating artists, and publishing opportunities require YOUTH_OFFICER." />
        </div>
      ) : null}
      {tab === 'registry' ? <RegistryTab canManage={canManage} /> : <OpportunitiesTab canManage={canManage} />}
    </div>
  );
}

function RegistryTab({ canManage }: { canManage: boolean }) {
  const [discipline, setDiscipline] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '100' });
  if (discipline) query.set('discipline', discipline);
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const artistsQ = useApiQuery<Page<ArtistRegistry>>(`/api/v1/m30/artists?${query.toString()}`);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = artistsQ.data?.items ?? [];
  const selected = items.find((a) => a.id === selectedId) ?? null;

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex flex-wrap gap-2">
          <input className="field-input max-w-xs" placeholder="Search name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
            <option value="">All disciplines</option>
            {DISCIPLINES.map((d) => (
              <option key={d} value={d}>
                {d.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Active only (default)</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
        {artistsQ.error ? <ErrorNote message={artistsQ.error} /> : null}
        <div className="card overflow-x-auto">
          {artistsQ.loading ? (
            <Spinner />
          ) : items.length === 0 ? (
            <EmptyState title="No artists found" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Discipline</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === a.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(a.id)}>
                    <td className="table-cell font-medium">{a.fullName}</td>
                    <td className="table-cell text-xs text-slate-500">{a.discipline.replaceAll('_', ' ')}</td>
                    <td className="table-cell">
                      <StatusBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="lg:col-span-2">
        {selected ? (
          <ArtistDetail key={selected.id} artist={selected} canManage={canManage} onChanged={artistsQ.reload} />
        ) : (
          <SectionCard>
            <EmptyState title="Select an artist" />
          </SectionCard>
        )}
      </div>
    </div>
  );
}

function ArtistDetail({ artist, canManage, onChanged }: { artist: ArtistRegistry; canManage: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matchesQ = useApiQuery<{ items: ArtOpportunity[] }>(`/api/v1/m30/artists/${artist.id}/opportunities/matches`);

  const deactivate = async () => {
    if (!confirm(`Deactivate ${artist.fullName}'s registration?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/artists/${artist.id}/deactivate`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to deactivate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title={artist.fullName}
        actions={
          canManage ? (
            <div className="flex gap-1.5">
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Close' : 'Edit'}
              </button>
              {artist.status === 'ACTIVE' ? (
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={deactivate} disabled={busy}>
                  Deactivate
                </button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        {editing ? (
          <EditArtistForm
            artist={artist}
            onDone={() => {
              setEditing(false);
              onChanged();
            }}
          />
        ) : (
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Discipline</dt>
              <dd className="font-medium text-slate-800">{artist.discipline.replaceAll('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Type</dt>
              <dd className="font-medium text-slate-800">{artist.artistType}</dd>
            </div>
            {artist.bio ? <p className="pt-1 text-xs text-slate-600">{artist.bio}</p> : null}
            {artist.portfolioLinks.length > 0 ? (
              <div className="pt-1">
                <dt className="mb-1 text-slate-500">Portfolio</dt>
                <ul className="space-y-0.5">
                  {artist.portfolioLinks.map((l) => (
                    <li key={l}>
                      <a className="text-xs text-brand-600 underline" href={l} target="_blank" rel="noreferrer">
                        {l}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </dl>
        )}
      </SectionCard>

      <SectionCard title="Matching open opportunities">
        {matchesQ.loading ? (
          <Spinner label="Loading…" />
        ) : matchesQ.error ? (
          <ErrorNote message={matchesQ.error} />
        ) : (matchesQ.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">No open opportunities currently match {artist.discipline.replaceAll('_', ' ').toLowerCase()}.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {matchesQ.data!.items.map((o) => (
              <li key={o.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <div className="font-medium">{o.title}</div>
                <div className="text-xs text-slate-500">Deadline {formatDate(o.applicationDeadline)}</div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function EditArtistForm({ artist, onDone }: { artist: ArtistRegistry; onDone: () => void }) {
  const [fullName, setFullName] = useState(artist.fullName);
  const [bio, setBio] = useState(artist.bio ?? '');
  const [portfolioLinks, setPortfolioLinks] = useState(artist.portfolioLinks.join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m30/artists/${artist.id}`, {
        fullName,
        bio: bio || undefined,
        portfolioLinks: portfolioLinks.split(',').map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Full name">
        <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </Field>
      <Field label="Bio">
        <textarea className="field-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
      </Field>
      <Field label="Portfolio links (comma separated URLs)">
        <input className="field-input" value={portfolioLinks} onChange={(e) => setPortfolioLinks(e.target.value)} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || fullName.trim().length < 2}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function OpportunitiesTab({ canManage }: { canManage: boolean }) {
  const [discipline, setDiscipline] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const oppsQ = useApiQuery<{ items: ArtOpportunity[] }>(`/api/v1/m30/art-opportunities${discipline ? `?discipline=${discipline}` : ''}`);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
          <option value="">All disciplines</option>
          {DISCIPLINES.map((d) => (
            <option key={d} value={d}>
              {d.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        {canManage ? (
          <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
            + Publish opportunity
          </button>
        ) : null}
      </div>
      {oppsQ.error ? <ErrorNote message={oppsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {oppsQ.loading ? (
          <Spinner />
        ) : (oppsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No open opportunities" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Discipline</th>
                <th className="table-head">Funding source</th>
                <th className="table-head">Deadline</th>
              </tr>
            </thead>
            <tbody>
              {oppsQ.data!.items.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="table-cell">
                    <div className="font-medium">{o.title}</div>
                    <div className="text-xs text-slate-500">{o.description}</div>
                  </td>
                  <td className="table-cell text-xs text-slate-500">{o.discipline.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{o.fundingSource ?? '—'}</td>
                  <td className="table-cell text-xs tabular-nums">{formatDate(o.applicationDeadline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateOpportunityModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            oppsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateOpportunityModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [discipline, setDiscipline] = useState<ArtDiscipline>('MUSIC');
  const [applicationDeadline, setApplicationDeadline] = useState(() => new Date(Date.now() + 30 * 24 * 3600_000).toISOString().slice(0, 10));
  const [fundingSource, setFundingSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/art-opportunities', {
        title,
        description,
        discipline,
        applicationDeadline: new Date(`${applicationDeadline}T23:59:59.000Z`).toISOString(),
        fundingSource: fundingSource || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to publish opportunity');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Publish an opportunity" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. National Art Gallery Emerging Artist Exhibition" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Discipline">
            <select className="field-input" value={discipline} onChange={(e) => setDiscipline(e.target.value as ArtDiscipline)}>
              {DISCIPLINES.map((d) => (
                <option key={d} value={d}>
                  {d.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Application deadline">
            <input type="date" className="field-input" value={applicationDeadline} onChange={(e) => setApplicationDeadline(e.target.value)} />
          </Field>
        </div>
        <Field label="Funding source (optional)">
          <input className="field-input" value={fundingSource} onChange={(e) => setFundingSource(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 2 || description.trim().length < 10}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
